import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getMaintenanceStatus } from "./api";
import type { MaintenanceUpcomingWindow } from "./types";

const DISMISSED_KEY = "maintenance_banner_dismissed";
const HOURS_AHEAD = 48;

function formatWindowRange(w: MaintenanceUpcomingWindow): string {
  try {
    const start = new Date(w.start_iso);
    const end = new Date(w.end_iso);
    const startStr = start.toLocaleString(undefined, {
      dateStyle: "short",
      timeStyle: "short",
    });
    const endStr = end.toLocaleString(undefined, {
      dateStyle: "short",
      timeStyle: "short",
    });
    return `${startStr} - ${endStr}`;
  } catch {
    return w.start_iso;
  }
}

function isWithinHours(iso: string, hours: number): boolean {
  try {
    const start = new Date(iso).getTime();
    const now = Date.now();
    return start > now && start - now <= hours * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export const UpcomingMaintenanceBanner: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [upcomingWindow, setUpcomingWindow] =
    useState<MaintenanceUpcomingWindow | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (location.pathname === "/maintenance") return;
    getMaintenanceStatus()
      .then((data) => {
        const next = data.upcoming_windows.find((w) =>
          isWithinHours(w.start_iso, HOURS_AHEAD),
        );
        if (next) {
          const dismissedId = sessionStorage.getItem(DISMISSED_KEY);
          if (dismissedId !== next.start_iso) {
            setUpcomingWindow(next);
          }
        }
      })
      .catch(() => {});
  }, [location.pathname]);

  const handleDismiss = useCallback(() => {
    if (upcomingWindow) {
      sessionStorage.setItem(DISMISSED_KEY, upcomingWindow.start_iso);
      setDismissed(true);
      setUpcomingWindow(null);
    }
  }, [upcomingWindow]);

  const showBanner = upcomingWindow && !dismissed;

  return (
    <>
      {showBanner && (
        <div
          className="bg-cp-yellow text-cp-black p-20 flex flex-row items-center justify-between gap-20 flex-wrap"
          role="alert"
        >
          <div className="flex-1 min-w-0 text-black">
            <span className="font-w-600">{t("scheduledMaintenance")} </span>
            <span>
              {formatWindowRange(upcomingWindow)}
              {upcomingWindow.message ? ` — ${upcomingWindow.message}` : ""}
            </span>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="shrink-0 border border-cp-black text-cp-black bg-transparent border-radius-5 px-20 py-10 font-w-600 cursor-pointer hover:opacity-90"
            aria-label={t("dismiss")}
          >
            {t("dismiss")}
          </button>
        </div>
      )}
      {children}
    </>
  );
};
